import { rollbar } from '@utils';

interface RetryConfig {
  maxAttempts?: number;
  delayMs?: number;
}

interface ErrorWithCode extends Error {
  code?: string;
}

interface ErrorWithStatus extends Error {
  status?: number;
}

interface RetryResult<T> {
  success: boolean;
  result?: T;
  error?: Error;
  attempts: number;
}

const defaultConfig: Required<RetryConfig> = {
  maxAttempts: 3,
  delayMs: 2000,
};

export function isRetryableError(error: Error): boolean {
  // Network errors
  if (error.name === 'NetworkError' || error.name === 'TypeError') {
    return true;
  }

  // Firebase function errors
  if ((error as ErrorWithCode).code) {
    const code = (error as ErrorWithCode).code;
    const retryableCodes = [
      'functions/deadline-exceeded',
      'functions/internal',
      'functions/resource-exhausted',
      'functions/unavailable',
      'functions/cancelled',
    ];
    return retryableCodes.includes(code!);
  }

  // HTTP status errors
  if ('status' in error) {
    const status = (error as ErrorWithStatus).status;
    return (
      status !== undefined &&
      ((status >= 500 && status <= 599) || status === 429 || status === 408)
    );
  }

  return false;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  shouldRetry: (error: Error) => boolean
): Promise<RetryResult<T>> {
  let attempts = 0;

  while (attempts < defaultConfig.maxAttempts) {
    try {
      attempts++;
      const result = await operation();
      return { success: true, result, attempts };
    } catch (error) {
      if (error instanceof Error) {
        // Don't retry if error is not retryable
        if (!shouldRetry(error)) {
          return { success: false, error, attempts };
        }

        // Don't retry if we've reached max attempts
        if (attempts === defaultConfig.maxAttempts) {
          rollbar.error('Retry failed', error);
          return { success: false, error, attempts };
        }

        await sleep(defaultConfig.delayMs);
      } else {
        throw error;
      }
    }
  }

  return {
    success: false,
    error: new Error('Max attempts reached'),
    attempts,
  };
}
